/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Dimensions,
  Alert,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome5,
} from '@expo/vector-icons';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const { width, height } = Dimensions.get('window');


const CoursePlayerScreen = ({ navigation, route }) => {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const { course, lesson, courseId, lessonId } = route.params;
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNotes, setShowNotes] = useState(false);

  // Sample lesson data with fallbacks
  const lessonData = {
    id: lesson?.id || lessonId || '1',
    title: lesson?.title || 'Engineering Lesson',
    duration: lesson?.duration || '15 min',
    type: lesson?.type || 'video',
    moduleTitle: lesson?.moduleTitle || 'Course Module',
    description: lesson?.description || 'This lesson covers important engineering concepts and practical applications.',
    resources: lesson?.resources || [
      { id: 1, title: 'Lesson Notes.pdf', type: 'pdf' },
      { id: 2, title: 'Practice Problems.docx', type: 'document' },
      { id: 3, title: 'Reference Materials.zip', type: 'archive' },
    ],
    transcript: lesson?.transcript || [
      { time: '00:00', text: 'Welcome to this engineering lesson...' },
      { time: '00:30', text: 'Today we will cover fundamental concepts...' },
      { time: '01:00', text: 'Let\'s start with the basic principles...' },
    ]
  };

  const courseData = {
    id: course?.id || courseId || '1',
    title: course?.title || 'Engineering Course',
    instructor: course?.instructor || 'Dr. Engineering Professor',
    color: course?.color || '#0e4e96',
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    // In a real app, this would control video/audio playback
  };

  const handleSeek = (seconds) => {
    setCurrentTime(Math.max(0, Math.min(duration, currentTime + seconds)));
  };

  const handleSpeedChange = () => {
    const speeds = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    setPlaybackSpeed(speeds[nextIndex]);
    Alert.alert('Playback Speed', `Speed set to ${speeds[nextIndex]}x`);
  };

  const handleDownloadResource = (resource) => {
    Alert.alert('Download', `Downloading ${resource.title}...`);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'pdf':
        return 'file-pdf-box';
      case 'document':
        return 'file-document';
      case 'archive':
        return 'archive';
      default:
        return 'file';
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#000000" />
      
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <View style={styles.headerInfo}>
          <Text style={styles.headerTitle} numberOfLines={1}>
            {lessonData.title}
          </Text>
          <Text style={styles.headerSubtitle}>
            {courseData.title}
          </Text>
        </View>
        <TouchableOpacity 
          style={styles.fullscreenButton}
          onPress={() => setIsFullscreen(!isFullscreen)}
        >
          <MaterialCommunityIcons 
            name={isFullscreen ? "fullscreen-exit" : "fullscreen"} 
            size={24} 
            color="#ffffff" 
          />
        </TouchableOpacity>
      </View>

      {/* Video Player */}
      <View style={[styles.videoContainer, isFullscreen && styles.fullscreenVideo]}>
        <View style={styles.videoPlayer}>
          <TouchableOpacity 
            style={styles.playButton}
            onPress={handlePlayPause}
          >
            <MaterialCommunityIcons 
              name={isPlaying ? "pause" : "play"} 
              size={64} 
              color="#ffffff" 
            />
          </TouchableOpacity>
          
          {/* Video Controls Overlay */}
          {showControls && (
            <View style={styles.controlsOverlay}>
              <View style={styles.topControls}>
                <Text style={styles.lessonTitle}>{lessonData.title}</Text>
              </View>
              
              <View style={styles.bottomControls}>
                <View style={styles.progressContainer}>
                  <View style={styles.progressBar}>
                    <View 
                      style={[
                        styles.progressFill, 
                        { width: `${(currentTime / (duration || 1)) * 100}%` }
                      ]} 
                    />
                  </View>
                  <Text style={styles.timeText}>
                    {formatTime(currentTime)} / {formatTime(duration || 900)}
                  </Text>
                </View>
                
                <View style={styles.controlButtons}>
                  <TouchableOpacity onPress={() => handleSeek(-10)}>
                    <MaterialCommunityIcons name="rewind-10" size={32} color="#ffffff" />
                  </TouchableOpacity>
                  
                  <TouchableOpacity onPress={handlePlayPause} style={styles.mainPlayButton}>
                    <MaterialCommunityIcons 
                      name={isPlaying ? "pause" : "play"} 
                      size={40} 
                      color="#ffffff" 
                    />
                  </TouchableOpacity>
                  
                  <TouchableOpacity onPress={() => handleSeek(10)}>
                    <MaterialCommunityIcons name="fast-forward-10" size={32} color="#ffffff" />
                  </TouchableOpacity>
                  
                  <TouchableOpacity onPress={handleSpeedChange}>
                    <Text style={styles.speedText}>{playbackSpeed}x</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}
        </View>
      </View>

      {!isFullscreen && (
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Lesson Info */}
          <View style={styles.lessonInfoCard}>
            <Text style={styles.sectionTitle}>About This Lesson</Text>
            <Text style={styles.lessonDescription}>{lessonData.description}</Text>
            
            <View style={styles.lessonMeta}>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="clock-outline" size={16} color="#64748B" />
                <Text style={styles.metaText}>{lessonData.duration}</Text>
              </View>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="play-circle-outline" size={16} color="#64748B" />
                <Text style={styles.metaText}>{lessonData.type}</Text>
              </View>
              <View style={styles.metaItem}>
                <MaterialCommunityIcons name="school" size={16} color="#64748B" />
                <Text style={styles.metaText}>{lessonData.moduleTitle}</Text>
              </View>
            </View>
          </View>

          {/* Tabs */}
          <View style={styles.tabsContainer}>
            <TouchableOpacity
              style={[styles.tab, !showNotes && styles.activeTab]}
              onPress={() => setShowNotes(false)}
            >
              <Text style={[styles.tabText, !showNotes && styles.activeTabText]}>
                Resources
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.tab, showNotes && styles.activeTab]}
              onPress={() => setShowNotes(true)}
            >
              <Text style={[styles.tabText, showNotes && styles.activeTabText]}>
                Transcript
              </Text>
            </TouchableOpacity>
          </View>

          {/* Tab Content */}
          <View style={styles.tabContent}>
            {!showNotes ? (
              <View>
                <Text style={styles.sectionTitle}>Lesson Resources</Text>
                {lessonData.resources.map((resource) => (
                  <TouchableOpacity
                    key={resource.id}
                    style={styles.resourceItem}
                    onPress={() => handleDownloadResource(resource)}
                  >
                    <View style={styles.resourceLeft}>
                      <MaterialCommunityIcons 
                        name={getResourceIcon(resource.type)} 
                        size={24} 
                        color="#0e4e96" 
                      />
                      <Text style={styles.resourceTitle}>{resource.title}</Text>
                    </View>
                    <MaterialCommunityIcons name="download" size={20} color="#64748B" />
                  </TouchableOpacity>
                ))}
              </View>
            ) : (
              <View>
                <Text style={styles.sectionTitle}>Lesson Transcript</Text>
                {lessonData.transcript.map((item, index) => (
                  <TouchableOpacity
                    key={index}
                    style={styles.transcriptItem}
                    onPress={() => setCurrentTime(parseInt(item.time.split(':')[0]) * 60 + parseInt(item.time.split(':')[1]))}
                  >
                    <Text style={styles.transcriptTime}>{item.time}</Text>
                    <Text style={styles.transcriptText}>{item.text}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}
          </View>
        </ScrollView>
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#000000',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 15,
    paddingVertical: 12,
    backgroundColor: '#000000',
  },
  backButton: {
    padding: 5,
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
    marginHorizontal: 15,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 12,
    color: '#ffffff',
    opacity: 0.7,
    marginTop: 2,
  },
  fullscreenButton: {
    padding: 5,
  },
  videoContainer: {
    height: 250,
    backgroundColor: '#000000',
  },
  fullscreenVideo: {
    height: height,
  },
  videoPlayer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  playButton: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  controlsOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'space-between',
    padding: 20,
  },
  topControls: {
    alignItems: 'center',
  },
  lessonTitle: {
    color: '#ffffff',
    fontSize: 18,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  bottomControls: {
    alignItems: 'center',
  },
  progressContainer: {
    width: '100%',
    marginBottom: 20,
  },
  progressBar: {
    height: 4,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 2,
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 2,
  },
  timeText: {
    color: '#ffffff',
    fontSize: 12,
    textAlign: 'center',
  },
  controlButtons: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mainPlayButton: {
    marginHorizontal: 30,
  },
  speedText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 20,
  },
  content: {
    flex: 1,
    backgroundColor: '#f0f4f8',
    padding: 15,
  },
  lessonInfoCard: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 12,
  },
  lessonDescription: {
    fontSize: 16,
    color: '#64748B',
    lineHeight: 24,
    marginBottom: 16,
  },
  lessonMeta: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  metaItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  metaText: {
    fontSize: 12,
    color: '#64748B',
    marginLeft: 4,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 4,
    marginBottom: 15,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
  },
  activeTabText: {
    color: '#ffffff',
  },
  tabContent: {
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.22,
    shadowRadius: 2.22,
    elevation: 3,
  },
  resourceItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  resourceLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  resourceTitle: {
    fontSize: 14,
    color: '#0e4e96',
    marginLeft: 12,
    flex: 1,
  },
  transcriptItem: {
    flexDirection: 'row',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f4f8',
  },
  transcriptTime: {
    fontSize: 12,
    color: '#19abb5',
    fontWeight: 'bold',
    width: 60,
    marginRight: 12,
  },
  transcriptText: {
    fontSize: 14,
    color: '#64748B',
    flex: 1,
    lineHeight: 20,
  },
});

export default CoursePlayerScreen;

